// FP 간트 — 바 hover 상세 툴팁(주문/자원 간트 공용)
//
// old(TreeGrid `OnGanttTip`)의 툴팁은 **항목과 내용이 두 열로 정렬**돼 있었다. 1차 이식은 행마다
// `flex + gap`이라 라벨 길이가 다르면 내용 시작 x가 행마다 달라져 읽기 어려웠다(2026-08-19 사용자 지적).
// → **2열 그리드**(`max-content 1fr`)로 바꿔 라벨 열 폭을 가장 긴 라벨에 맞추고 내용은 한 열에서 시작한다.
//
// 두 화면에 같은 마크업이 복제돼 있던 것을 여기로 합쳤다 — 한쪽만 고쳐 스타일이 갈라지는 것을 막는다.
//
// 2026-08-21 — **정보 위계 개편**(설계·결정 경과: reports/gantt-implementation.md §7-3,
// 시안: docs/fp-frontend-migration/prototypes/gantt-tooltip-styles.html). 모든 항목이 같은 무게로
// 나열되면 무엇을 먼저 봐야 할지 알 수 없다는 지적에서 출발해 3단으로 나눴다:
//   ① 헤더 — 종류 아이콘 + 캡션 + 식별 코드 + 상태 뱃지
//   ② 기간 그룹 — 시작·종료·소요·납기
//   ③ 부가 그룹 — 판매 주문/거래처 등(한 단 작은 글씨)
// 크기·색은 전부 `ganttConstants`의 `UI.tip*` / `LATE_TEXT_*` / `ONTIME_TEXT_*` / `CAUTION_TEXT_COLOR`
// 토큰에서 온다(2026-08-21 점검 — 크기 5단계 → 3단계, 지연색 화면 내 2종 통일, 값 색을 캔버스 `cellFg`와 통일).
//
// 2026-08-21(2) — **자원 간트도 위계형으로 전환 완료.** 두 화면이 모두 `groups`를 쓰게 되어
// 과도기용 `rows`(단일 평면 목록) 경로는 제거했다. 남은 입력은 `groups`(위계형) / `text`(단문,
// 좌측 자원 셀) / `tools`(Tool 사용 정보)뿐이다.

import { Fragment } from "react";

import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Typography from "@mui/material/Typography";

import GanttDetailRows, { TONE_COLORS } from "@plannel/pages/factory-plan/common/gantt/GanttDetailRows";
import {
  CAUTION_TEXT_COLOR,
  LATE_TEXT_COLOR,
  UI
} from "@plannel/pages/factory-plan/common/gantt/ganttConstants";

// 크기·색은 전부 `UI`/상태색 토큰에서 온다 — 툴팁이 자기 색을 따로 갖고 있어서 같은 화면 안에서
// "지연"이 두 색이 됐던 문제(2026-08-21 점검)를 막는다. 여기에 색·크기를 직접 쓰지 말 것.
const LABEL_SX = { color: "text.secondary", whiteSpace: "nowrap", fontSize: UI.tipBodyFontSize };
const VALUE_SX = { whiteSpace: "nowrap", color: UI.tipValueFg, fontSize: UI.tipBodyFontSize };

// 종류 아이콘 — 옛 색상 네모를 대체한다. 간트 바가 이미 색으로 종류를 말하고 있어서 툴팁에서 색을
// 한 번 더 쓰면 정보가 중복되고, 색이 캡션보다 먼저 눈에 들어와 위계가 흐려진다(2026-08-21 사용자 판단).
// 그래서 **색이 아니라 형태**로 종류를 알린다 → 아이콘 색은 캡션과 같은 `text.secondary` 고정.
const KIND_ICONS = {
  order: AssignmentOutlinedIcon,          // 작업 주문 = 작업 지시서(클립보드)
  activity: PrecisionManufacturingOutlinedIcon  // 작업 = 설비에서 돌아가는 공정
};

// 카드 좌우 padding — 밴드 음영을 카드 폭 끝까지 빼려면 이 값의 음수 마진이 필요하다.
const PAD_X = 1.75;
const PAD_Y = 1.5;

// 최상단 밴드 강조(2026-08-21). 밴드에는 "이 바가 무엇인가"(종류·코드·품목)와 "얼마인가"(수량),
// "문제가 있나"(뱃지)가 들어가고 아래 값들은 전부 "언제"다 → 밴드만 한 단 올리면 시선 순서가
// "무엇·얼마 → 문제 → 언제"로 고정된다.
// **음영은 무채색**이다 — 색은 간트 바가 이미 쓰고 있어서 여기서 또 쓰면 경쟁한다(§7-3a).
const BAND_BG = UI.tipBandBg;

// 밴드는 **헤더 + 첫 요약 구역(품목·수량)까지** 덮는다. 그래서 헤더 자체는 아래 경계선을 갖지 않고
// (밴드가 첫 구역으로 이어진다) 경계선은 첫 구역이 갖는다.
const BAND_HEAD_SX = {
  mt: -PAD_Y,
  mx: -PAD_X,
  px: PAD_X,
  pt: PAD_Y,
  // 코드 줄과 요약 행 사이 간격 — 같은 밴드 안이라 넓게 벌리면 두 덩어리로 보인다(2026-08-21 조정).
  pb: 0.75,
  backgroundColor: BAND_BG
};

// 구역 하나 = subgrid. **부모의 열 트랙을 그대로 쓰면서** 자기 배경·경계선을 가질 수 있다.
// 구역별로 새 grid를 만들면 `max-content` 라벨 열이 구역마다 따로 계산돼 값 열 x가 어긋난다.
// 좌우 `mx: -PAD_X` + `px: PAD_X`는 서로 상쇄되므로 배경을 카드 폭 끝까지 빼도 트랙 정렬이 유지된다.
const SECTION_SX = {
  display: "grid",
  gridTemplateColumns: "subgrid",
  gridColumn: "1 / -1",
  rowGap: 0.25,
  alignItems: "baseline",
  mx: -PAD_X,
  px: PAD_X,
  // subgrid 미지원 브라우저 폴백 — 구역별 폭 계산으로 되돌아간다(정렬만 느슨해지고 깨지지는 않는다).
  "@supports not (grid-template-columns: subgrid)": {
    gridTemplateColumns: "max-content 1fr",
    columnGap: 2
  }
};

const KindHeader = ({ kind, captionKey, code, badge, t }) => {
  const Icon = KIND_ICONS[kind];
  const tone = TONE_COLORS[badge?.tone] ?? TONE_COLORS.warn;

  return (
    <Box sx={BAND_HEAD_SX}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.875 }}>
        {Icon ? <Icon sx={{ fontSize: 14, color: "text.secondary" }} /> : null}
        <Typography
          variant="caption"
          sx={{
            fontSize: UI.tipSmallFontSize, fontWeight: 600, letterSpacing: "0.02em",
            color: "text.secondary", whiteSpace: "nowrap"
          }}
        >
          {t(captionKey)}
        </Typography>

        <Box sx={{ flex: "1 1 auto" }} />

        {badge ? (
          <Typography
            variant="caption"
            sx={{
              fontSize: UI.tipSmallFontSize, fontWeight: 600, px: 1, py: 0.25, borderRadius: 999,
              whiteSpace: "nowrap", color: tone.fg, backgroundColor: tone.bg,
              // **테두리가 알약을 보이게 하는 유일한 수단이다**(2026-08-26). 뱃지 배경(`tone.bg`)은
              // 흰 배경 기준으로 고른 밝은 틴트라 밴드(`tipBandBg #e8f5f7`) 위에서는 대비가
              // **1.00**(준수) / 1.04(지연) — 밝기가 같아 알약이 통째로 묻힌다. 글자 대비는 4.86 /
              // 5.10으로 원래부터 정상이었다("글자가 안 보인다"가 아니라 "뱃지로 안 보인다"였음).
              //   배경을 진하게 하는 방법은 못 쓴다 — 알약이 보일 만큼(1.29) 진하게 하면 글자 대비가
              //   3.74로 떨어져 AA(4.5)를 깬다. 둘을 동시에 만족하는 지점이 없다.
              //   솔리드로 채우는 방법도 배제했다 — 색 면적이 커져 간트 바 색과 경쟁하고(§7-3a),
              //   정상 상태인 "준수"가 경고처럼 읽힌다.
              // 선 색은 글자색과 같은 `tone.fg`라 밴드 대비 4.84 / 4.89로 기준(3.0)을 넉넉히 넘고,
              // 색 면적이 늘지 않는다. 시안 비교: prototypes/gantt-tooltip-status-badge.html
              border: "1px solid", borderColor: tone.fg
            }}
          >
            {t(badge.labelKey)}
          </Typography>
        ) : null}
      </Box>

      {code ? (
        // 식별 코드 = 툴팁에서 가장 중요한 한 줄. 본문(12.5px)과 확실히 차이가 나야 강조로 읽힌다.
        <Typography
          sx={{
            fontSize: UI.tipCodeFontSize, fontWeight: 700, letterSpacing: "-0.01em",
            lineHeight: 1.3, color: UI.tipValueFg
          }}
        >
          {code}
        </Typography>
      ) : null}
    </Box>
  );
};

/**
 * tooltip = null | {
 *   x, y,                      // 커서 위치(가상 앵커)
 *
 *   // ── 위계형(주문 간트) ──
 *   kind,                      // "order" | "activity" — 헤더 아이콘 결정
 *   captionKey,                // 종류 캡션 = 랭팩 키
 *   code,                      // 식별 코드(헤더)
 *   badge,                     // { labelKey, tone: "warn" | "ok" } — 상태 플래그만. 수치는 행에
 *   groups,                    // [{ small?, rows: [{ labelKey, value, wrap?, checkbox?, tone? }] }]
 *
 *   // ── 단문 / 부가 구역 ──
 *   text,                      // 단문 툴팁(좌측 자원 코드 셀의 자원명) — groups 대신 쓴다
 *   tools: [{ toolCd, used, avail, status }]   // Tool 사용 정보(자원 간트). status: "ok"|"tight"|"over"
 * }
 */
const GanttTooltip = ({ tooltip, t }) => (
  <Popper
    open={Boolean(tooltip)}
    placement="right-start"
    anchorEl={tooltip ? { getBoundingClientRect: () => new DOMRect(tooltip.x, tooltip.y, 0, 0) } : null}
    modifiers={[{ name: "offset", options: { offset: [8, 12] } }]}
    sx={{ zIndex: 1500, pointerEvents: "none" }}
  >
    {/* `overflow: hidden` — 밴드 음영이 카드 라운드 코너 밖으로 새지 않게 한다.
        셸(테두리·그림자·라운드)은 `UI.tipCard*` — 앱 팝업 표준값이다. `elevation`만 쓰면 흰 배경
        위에서 경계가 사라진다(ganttConstants의 해당 주석 참고). */}
    <Paper
      elevation={0}
      sx={{
        px: PAD_X, py: PAD_Y, minWidth: tooltip?.text ? 0 : 240, overflow: "hidden",
        borderRadius: UI.tipCardRadius, border: UI.tipCardBorder, boxShadow: UI.tipCardShadow
      }}
    >
      {tooltip?.text ? (
        <Typography variant="caption" sx={VALUE_SX}>{tooltip.text}</Typography>
      ) : null}

      {tooltip?.captionKey ? (
        <KindHeader
          kind={tooltip.kind}
          captionKey={tooltip.captionKey}
          code={tooltip.code}
          badge={tooltip.badge}
          t={t}
        />
      ) : null}

      {(tooltip?.groups?.length ?? 0) > 0 && (
        // 바깥 그리드가 열 트랙을 정의하고, 각 구역이 subgrid로 그 트랙을 공유한다
        // → 라벨 열 폭이 모든 구역에서 같다(구역이 자기 배경을 가져도 유지된다).
        <Box sx={{ display: "grid", gridTemplateColumns: "max-content 1fr", columnGap: 2 }}>
          {tooltip.groups.map((group, index) => (
            <Box
              key={group.rows.map(r => r.labelKey).join("|")}
              sx={{
                ...SECTION_SX,
                // 첫 구역(요약: 품목·수량) = 밴드의 아랫부분. 헤더와 배경이 이어지고 경계선을 여기서 닫는다.
                ...(index === 0
                  ? { backgroundColor: BAND_BG, borderBottom: "1px solid", borderColor: "divider", pt: 0.25, pb: 1 }
                  : null),
                // 밴드 바로 다음 구역은 밴드 경계선이 이미 있으므로 선을 겹치지 않고 여백만 준다.
                ...(index === 1 ? { mt: 1.25 } : null),
                ...(index > 1 ? { mt: 1.25, pt: 1.25, borderTop: "1px solid", borderColor: "divider" } : null)
              }}
            >
              <GanttDetailRows rows={group.rows} small={group.small} t={t} />
            </Box>
          ))}
        </Box>
      )}

      {(tooltip?.tools?.length ?? 0) > 0 && (
        <>
          <Divider sx={{ my: 1 }} />

          {/* 구역 제목 줄 — 왼쪽은 "무엇"(툴 사용 정보 + 개수), 오른쪽은 아래 숫자 쌍이 **무엇/무엇**인지
              알려주는 열 제목이다. `2 / 5`만 있으면 두 숫자의 정체를 알 수 없다(2026-08-21 사용자 시안). */}
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 0.5 }}>
            <Typography variant="caption" sx={LABEL_SX}>{t("FP_TOOL_USAGE_INFO")}</Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: UI.tipSmallFontSize, fontWeight: 700, color: "text.secondary",
                backgroundColor: UI.tipBandBg, borderRadius: 0.75, px: 0.625, lineHeight: 1.5
              }}
            >
              {tooltip.tools.length}
            </Typography>
            <Box sx={{ flex: "1 1 auto" }} />
            <Typography variant="caption" sx={{ ...LABEL_SX, fontSize: UI.tipSmallFontSize }}>
              {t("FP_TOOL_USED_AVAIL")}
            </Typography>
          </Box>

          {/* Tool 행은 위 그룹들과 **열 규칙이 다르다** — 라벨(툴 코드)이 값이고 숫자 쌍이 열 제목 아래
              오른쪽에 붙어야 읽힌다. 그래서 `1fr max-content`로 두고 값을 우측 정렬한다. */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr max-content", columnGap: 2, rowGap: 0.25, alignItems: "baseline" }}>
            {tooltip.tools.map(tool => (
              <Fragment key={tool.toolCd}>
                <Typography variant="caption" sx={VALUE_SX}>{tool.toolCd}</Typography>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, justifySelf: "end" }}>
                  {tool.status === "ok" ? null : (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: UI.tipSmallFontSize, fontWeight: 600,
                        color: tool.status === "over" ? LATE_TEXT_COLOR : CAUTION_TEXT_COLOR
                      }}
                    >
                      {t(tool.status === "over" ? "FP_OVER" : "FP_TIGHT")}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    sx={{ ...VALUE_SX, fontVariantNumeric: "tabular-nums" }}
                  >
                    {`${tool.used} / ${tool.avail}`}
                  </Typography>
                </Box>
              </Fragment>
            ))}
          </Box>
        </>
      )}
    </Paper>
  </Popper>
);

export default GanttTooltip;
